import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Simple module-level cache so ProviderCard/ServiceCard don't re-fetch on
// every mount. Loaded once per logged-in session.
let cache = null;    // { kind: Set<target_id> }
let loading = null;  // in-flight promise

async function loadOnce() {
  if (cache) return cache;
  if (loading) return loading;
  loading = (async () => {
    try {
      const { data } = await api.get("/favorites");
      const providers = new Set();
      const services = new Set();
      (data || []).forEach((f) => {
        if (f.kind === "provider") providers.add(f.target_id);
        else if (f.kind === "service") services.add(f.target_id);
      });
      cache = { provider: providers, service: services };
      return cache;
    } catch (_) {
      cache = { provider: new Set(), service: new Set() };
      return cache;
    } finally { loading = null; }
  })();
  return loading;
}

export function invalidateFavorites() { cache = null; }

export function useFavorite(kind, targetId) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const setLocal = useCallback((val) => {
    setSaved(val);
    if (cache) {
      if (val) cache[kind]?.add(targetId);
      else cache[kind]?.delete(targetId);
    }
  }, [kind, targetId]);

  useEffect(() => {
    if (!user || !targetId) return;
    let cancelled = false;
    loadOnce().then((c) => {
      if (cancelled) return;
      setSaved(!!c[kind]?.has(targetId));
    });
    return () => { cancelled = true; };
  }, [user?.id, kind, targetId]);

  return [saved, setLocal];
}
